import * as React from "react";
import { PERMISSIONS } from "@/constants";
import {
	Alert,
	Box,
	Button,
	ButtonBase,
	Card,
	CardContent,
	Chip,
	LinearProgress,
	Link,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { ArrowLeftIcon, MagnifyingGlassIcon, PlugsConnectedIcon, PlusIcon } from "@phosphor-icons/react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
	useBuyLeads,
	useEditLeadSearch,
	useLeadPurchase,
	useLeadSearch,
	useLeadSearches,
	useReplyToLeadSearch,
	useStartLeadSearch,
	type LeadSearch,
} from "@/hooks/api/use-lead-finder";
import { useOAuthStatus } from "@/hooks/api/use-oauth";
import { usePluginsList } from "@/hooks/api/use-plugins";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { useAuth } from "@/components/core/auth-provider";
import { RouterLink } from "@/components/core/link";

import { AudienceSummary } from "./audience-summary";
import { LeadChat } from "./lead-chat";
import { LeadFieldsPrompt } from "./lead-fields-prompt";
import { LeadPreviewTable } from "./lead-preview-table";
import { PurchaseCard } from "./purchase-card";
import { RecentSearchesList, RecentSearchesMenu } from "./recent-searches";
import { SearchSummary } from "./search-summary";

/** A purchase started longer ago than this has finished: the page does not ask after it. */
const RUNNING_MS = 60 * 60 * 1000;

/** Starting points on the empty page, by what the calls are for. */
const STARTERS = [
	{ goal: "Book appointments", text: "Dental practice owners in Melbourne, to pitch our voice agents" },
	{ goal: "Book demos", text: "500 IT leaders using Microsoft Teams, to book Poly headset demos" },
	{ goal: "Fill a pipeline", text: "HR directors at companies with over 500 staff in Texas" },
];

function Unavailable({ owner }: { owner: boolean }): React.JSX.Element {
	const navigate = useNavigate();
	return (
		<Card>
			<CardContent>
				<Stack spacing={2} sx={{ alignItems: "flex-start" }}>
					<PlugsConnectedIcon size={32} />
					<Typography variant="h6">Connect ZoomInfo to find leads</Typography>
					<Typography variant="body2" color="text.secondary">
						{owner
							? "Leads come from your own ZoomInfo account. Add a ZoomInfo plugin, connect your account and enable it, then come back here."
							: "Leads come from your company's ZoomInfo account, which only your account owner can open here. Ask them to find leads for you."}
					</Typography>
					{owner && (
						<Button variant="contained" onClick={() => navigate("/plugins")}>
							Go to plugins
						</Button>
					)}
				</Stack>
			</CardContent>
		</Card>
	);
}

interface WelcomeProps {
	brief: string;
	onBrief: (brief: string) => void;
	onSend: (text: string) => void;
}

/** The empty page: one box to say who to call, like the first line of a chat, and starting points under it. */
function Welcome({ brief, onBrief: setBrief, onSend }: WelcomeProps): React.JSX.Element {
	const submit = () => brief.trim() && onSend(brief.trim());
	return (
		<Stack spacing={2.5} sx={{ flex: "999 1 520px", minWidth: 0 }}>
			<Box>
				<Typography sx={{ fontSize: 28, lineHeight: "36px", fontWeight: 700, letterSpacing: "-0.02em" }}>
					Who do you want your agent to call?
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
					Say who and why. The lead finder asks only what it needs, searches ZoomInfo for free and shows you who fits
					before you spend any credits.
				</Typography>
			</Box>
			<Stack
				component="form"
				direction="row"
				spacing={1}
				sx={{
					alignItems: "flex-end",
					border: "1.5px solid var(--mui-palette-primary-main)",
					borderRadius: "16px",
					bgcolor: "background.paper",
					p: 1,
					pl: 2,
					boxShadow: "0 6px 24px rgba(var(--mui-palette-primary-mainChannel) / 0.12)",
				}}
				onSubmit={(event: React.FormEvent) => {
					event.preventDefault();
					submit();
				}}
			>
				<TextField
					fullWidth
					multiline
					maxRows={4}
					variant="standard"
					placeholder="e.g. I want to find dentists to call and pitch our voice agents"
					value={brief}
					onChange={(event) => setBrief(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter" && !event.shiftKey) {
							event.preventDefault();
							submit();
						}
					}}
					inputProps={{ maxLength: 1000, "aria-label": "Who do you want to call?" }}
					InputProps={{ disableUnderline: true, sx: { fontSize: 16, py: 1.25 } }}
				/>
				<Button
					type="submit"
					variant="contained"
					startIcon={<MagnifyingGlassIcon />}
					disabled={!brief.trim()}
					sx={{ flexShrink: 0, height: 44, borderRadius: "10px" }}
				>
					Find leads
				</Button>
			</Stack>
			<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.25 }}>
				{STARTERS.map((starter) => (
					<ButtonBase
						key={starter.goal}
						onClick={() => setBrief(starter.text)}
						sx={{
							textAlign: "left",
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
							gap: 0.5,
							p: 1.75,
							borderRadius: "12px",
							border: "1px solid var(--mui-palette-divider)",
							bgcolor: "background.paper",
							"&:hover": { borderColor: "primary.main" },
						}}
					>
						<Typography variant="caption" sx={{ fontWeight: 600, color: "primary.main" }}>
							{starter.goal}
						</Typography>
						<Typography variant="body2">{starter.text}</Typography>
					</ButtonBase>
				))}
			</Box>
		</Stack>
	);
}

export function FindLeads(): React.JSX.Element {
	const { permissions } = useAuth();
	const owner = Boolean(permissions[PERMISSIONS.plugin]);
	const { data: plugins, isLoading: pluginsLoading } = usePluginsList("limit=100&offset=0", owner);
	const zoominfo = plugins?.data.find((plugin) => plugin.plugin.name === "zoominfo" && plugin.enabled);
	const { data: connection, isLoading: connectionLoading } = useOAuthStatus(zoominfo?.id ?? "");
	const pluginId = zoominfo?.id ?? "";

	const [params, setParams] = useSearchParams();
	const searchId = params.get("search");
	const { data: conversation, isLoading: opening, isError: openFailed } = useLeadSearch(pluginId, searchId);
	const { data: recent = [] } = useLeadSearches(pluginId);
	const start = useStartLeadSearch(pluginId);
	const reply = useReplyToLeadSearch(pluginId);
	const edit = useEditLeadSearch(pluginId);
	const buy = useBuyLeads(pluginId);
	/** The customer's line on its way to the finder. */
	const [pending, setPending] = React.useState<string | null>(null);
	/** The first line, kept to send again if it fails. */
	const [brief, setBrief] = React.useState("");
	const [purchaseId, setPurchaseId] = React.useState<string | null>(null);
	/** Bumped for each new search, so the purchase form starts from that search's count and name. */
	const [version, setVersion] = React.useState(0);

	// Another search opened, or a new one begun: nothing of the last one's purchase carries over.
	React.useEffect(() => setPurchaseId(null), [searchId]);
	// Back on a search whose last purchase is still running: follow it rather than offer to buy again.
	const last = conversation?.purchases.at(-1);
	const latest = !purchaseId && last && Date.now() - Date.parse(last.createdAt) < RUNNING_MS ? last.id : null;
	const { data: latestPurchase, isError: latestLost } = useLeadPurchase(pluginId, latest);
	/** The last purchase is not known to have ended yet: buying again waits for its answer. */
	const checking = latest !== null && !latestPurchase && !latestLost;

	const thinking = start.isPending || reply.isPending;
	const busy = thinking || edit.isPending;
	/** A purchase starting or under way: the search it was made from cannot change. */
	const buying = buy.isPending || purchaseId !== null || checking;
	const result = conversation?.result ?? null;
	/** A first line answered after the customer left: its search must not land on the page they went to. */
	const mounted = React.useRef(true);
	React.useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	React.useEffect(() => {
		if (latest && (latestPurchase?.state === "waiting" || latestPurchase?.state === "active")) setPurchaseId(latest);
	}, [latest, latestPurchase?.state]);

	const send = async (text: string) => {
		setPending(text);
		try {
			const next =
				searchId && conversation
					? await reply.mutateAsync({ id: searchId, version: conversation.version, text })
					: await start.mutateAsync(text);
			if (!searchId && mounted.current) setParams({ search: next.id });
			if (next.result) setVersion((current) => current + 1);
			setBrief("");
			return true;
		} catch {
			if (!searchId) setBrief(text);
			return false; // the hook has said why
		} finally {
			setPending(null);
		}
	};
	const handleEdit = (search: LeadSearch) =>
		searchId &&
		conversation &&
		edit.mutate(
			{ id: searchId, version: conversation.version, search },
			{ onSuccess: () => setVersion((current) => current + 1) }
		);
	const handleBuy = async (input: { count: number; groupName: string }) => {
		if (!searchId || !conversation) return;
		const { id } = await buy.mutateAsync({ searchId, version: conversation.version, ...input });
		setPurchaseId(id);
	};
	const newSearch = () => {
		setPending(null);
		setParams({});
	};
	const open = (id: string) => {
		setPending(null);
		setParams({ search: id });
	};

	const loading = pluginsLoading || (Boolean(zoominfo) && connectionLoading);
	const ready = Boolean(zoominfo) && connection?.status === "connected";
	const started = Boolean(searchId) || pending !== null;
	const bought = conversation?.purchases.reduce((sum, { count }) => sum + count, 0) ?? 0;

	const purchase =
		result && result.total > 0 && conversation ? (
			<PurchaseCard
				key={`${searchId}:${version}`}
				pluginId={pluginId}
				search={result.search}
				total={result.total}
				busy={busy || checking}
				starting={buy.isPending}
				purchaseId={purchaseId}
				purchases={conversation.purchases}
				onBuy={handleBuy}
				onClose={() => setPurchaseId(null)}
			/>
		) : null;

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={3}>
				<Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: "flex-end", flexWrap: "wrap" }}>
					<Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
						<Breadcrumbs>
							<Link
								color="text.secondary"
								component={RouterLink}
								href="/contacts"
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Contacts
							</Link>
						</Breadcrumbs>
						<Stack direction="row" spacing={1.25} sx={{ alignItems: "center", mt: 1, flexWrap: "wrap" }}>
							<Typography
								component="h1"
								sx={{ fontSize: 24, lineHeight: "32px", fontWeight: 600, letterSpacing: "-0.01em" }}
							>
								{conversation?.title ?? "Find leads"}
							</Typography>
							{conversation && (
								<Chip
									size="small"
									label={bought ? `${bought.toLocaleString()} leads bought` : "Draft · nothing bought yet"}
									color={bought ? "success" : "default"}
									variant={bought ? "filled" : "outlined"}
								/>
							)}
						</Stack>
					</Box>
					{ready && started && (
						<Stack direction="row" spacing={1.5}>
							<RecentSearchesMenu
								searches={recent.filter(({ id }) => id !== searchId)}
								onOpen={(id) => !thinking && open(id)}
							/>
							<Button variant="outlined" startIcon={<PlusIcon />} onClick={newSearch} disabled={thinking}>
								New search
							</Button>
						</Stack>
					)}
				</Stack>

				{loading || (searchId && opening) ? (
					<LinearProgress />
				) : ready ? (
					openFailed ? (
						<Alert
							severity="warning"
							action={
								<Button color="inherit" size="small" onClick={newSearch}>
									New search
								</Button>
							}
						>
							That search could not be opened.
						</Alert>
					) : started ? (
						<Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: "wrap", alignItems: "flex-start" }}>
							<Box
								sx={{
									flex: "1 1 340px",
									maxWidth: { md: 440 },
									minWidth: 0,
									position: { md: "sticky" },
									top: { md: 88 },
								}}
							>
								<LeadChat
									messages={conversation?.messages ?? []}
									pending={pending}
									thinking={thinking}
									busy={edit.isPending}
									disabled={buying}
									onSend={send}
								/>
							</Box>
							<Box sx={{ flex: "999 1 560px", minWidth: 0 }}>
								{result ? (
									<Stack spacing={2.5} sx={{ opacity: busy ? 0.6 : 1, transition: "opacity 150ms" }}>
										{edit.isPending && <LinearProgress />}
										{purchaseId && purchase}
										<AudienceSummary
											total={result.total}
											preview={result.preview}
											newsCompanies={result.newsCompanies}
										/>
										{result.rejected && (
											<Alert severity="warning">ZoomInfo could not run this search: {result.rejected}</Alert>
										)}
										{!purchaseId && result.total > 0 && <LeadFieldsPrompt pluginId={pluginId} />}
										<SearchSummary
											search={result.search}
											unknown={result.unknown}
											busy={busy || buying}
											onEdit={handleEdit}
										/>
										{result.preview.length > 0 ? (
											<LeadPreviewTable total={result.total} leads={result.preview} />
										) : (
											!result.rejected && (
												<Alert severity="warning">
													ZoomInfo has no contacts for this search. Remove a filter or tell the lead finder how to widen
													it.
												</Alert>
											)
										)}
										{!purchaseId && purchase}
									</Stack>
								) : conversation?.notice && !thinking ? (
									<Alert severity="warning">
										{conversation.notice} Send a message and the lead finder will search again.
									</Alert>
								) : (
									<Card sx={{ borderRadius: "16px" }}>
										<CardContent>
											<Typography variant="body2" color="text.secondary">
												{thinking
													? "Working out who fits..."
													: "Answer the lead finder and the people who fit appear here, with a free preview. Nothing is bought until you say so."}
											</Typography>
										</CardContent>
									</Card>
								)}
							</Box>
						</Stack>
					) : (
						<Stack direction="row" spacing={4} useFlexGap sx={{ flexWrap: "wrap", alignItems: "flex-start", pt: 2 }}>
							<Welcome brief={brief} onBrief={setBrief} onSend={(text) => void send(text)} />
							<Box sx={{ flex: "1 1 300px", minWidth: 0 }}>
								<RecentSearchesList searches={recent} onOpen={open} />
							</Box>
						</Stack>
					)
				) : (
					<Unavailable owner={owner} />
				)}
			</Stack>
		</Box>
	);
}
